import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import type { Property } from "@shared/schema";
import { sortProperties } from "@/lib/property-utils";
import { X, Check, Loader2, AlertCircle } from "lucide-react";
import { PdfPreview } from "@/components/pdf-preview";

const EXPENSE_CATEGORIES = [
  { label: "Mortgage", value: "mortgage" },
  { label: "Property Tax", value: "property_tax" },
  { label: "Insurance", value: "insurance" },
  { label: "HOA", value: "hoa" },
  { label: "Utilities - Electric", value: "utilities_electric" },
  { label: "Utilities - Gas", value: "utilities_gas" },
  { label: "Utilities - Water", value: "utilities_water" },
  { label: "Utilities - Sewer", value: "utilities_sewer" },
  { label: "Utilities - Trash", value: "utilities_trash" },
  { label: "Utilities - Internet", value: "utilities_internet" },
  { label: "CapEx", value: "capex" },
  { label: "Repairs & Maintenance", value: "repairs_maintenance" },
  { label: "Cleaning", value: "cleaning" },
  { label: "Lawn Care / Landscaping", value: "lawn_care" },
  { label: "Pest Control", value: "pest_control" },
  { label: "Property Management", value: "property_management" },
  { label: "Furnishings", value: "furnishings" },
  { label: "Supplies", value: "supplies" },
  { label: "Other", value: "other" },
];

const DOC_TYPE_LABELS: Record<string, string> = {
  invoice: "Invoice",
  expense: "Expense / Receipt",
  bid: "Contractor Bid",
  contract: "Contract",
  other: "Other",
};

interface MailDocMeta {
  id: number;
  agentmailAttachmentName: string;
  senderEmail: string | null;
  senderName: string | null;
  receivedAt: string | null;
  mimeType: string;
  status: string;
  geminiDocumentType: string | null;
  extractedData: Record<string, any> | null;
  suggestedPropertyId: number | null;
}

interface Props {
  doc: MailDocMeta;
  properties: Property[];
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onDone: () => void;
}

export function MailDocumentReviewDialog({ doc, properties, open, onOpenChange, onDone }: Props) {
  const { toast } = useToast();

  const [propertyId, setPropertyId] = useState<string>(() =>
    String(doc.suggestedPropertyId ?? properties[0]?.id ?? "")
  );
  const [docType, setDocType] = useState<string>(doc.geminiDocumentType ?? "other");
  const [vendorName, setVendorName] = useState<string>(doc.extractedData?.vendorName ?? "");
  const [amount, setAmount] = useState<string>(
    doc.extractedData?.totalAmount != null ? String(doc.extractedData.totalAmount) : ""
  );
  const [date, setDate] = useState<string>(doc.extractedData?.date ?? "");
  const [invoiceNumber, setInvoiceNumber] = useState<string>(doc.extractedData?.invoiceNumber ?? "");
  const [category, setCategory] = useState<string>(doc.extractedData?.category ?? "other");
  const [description, setDescription] = useState<string>(
    doc.extractedData?.description ?? doc.extractedData?.scopeSummary ?? ""
  );

  // The /file endpoint streams the binary directly with inline Content-Disposition,
  // so we point the iframe/img at it instead of fetching a data URL via JSON.
  const fileUrl = `/api/app/mail-documents/${doc.id}/file`;

  const approveMutation = useMutation({
    mutationFn: async () => {
      const pidNum = parseInt(propertyId);
      if (!pidNum) throw new Error("Please select a property first.");
      return apiRequest("POST", `/api/app/mail-documents/${doc.id}/approve`, {
        propertyId: pidNum,
        documentType: docType,
        vendorName: vendorName || undefined,
        amount: amount ? parseFloat(amount) : undefined,
        date: date || undefined,
        category: category || undefined,
        description: description || undefined,
      });
    },
    onSuccess: () => {
      toast({ title: "Document approved", description: "Committed to property documents." });
      queryClient.invalidateQueries({ queryKey: ["/api/app/mail-documents"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/mail-documents/pending-count"] });
      // Refresh property-documents so the Documents tab reflects the newly committed file
      queryClient.invalidateQueries({ queryKey: ["/api/app/property-documents"] });
      // Refresh expenses so the Expenses tab + P&L pick up the new auto-created expense
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
      onDone();
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast({ title: "Approval failed", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" });
    },
  });

  const dismissMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      return apiRequest("POST", `/api/app/mail-documents/${doc.id}/dismiss`, {});
    },
    onSuccess: () => {
      toast({ title: "Document dismissed" });
      queryClient.invalidateQueries({ queryKey: ["/api/app/mail-documents"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/mail-documents/pending-count"] });
      onDone();
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast({ title: "Dismiss failed", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" });
    },
  });

  const isPdf = (doc.mimeType || "").includes("pdf");
  const busy = approveMutation.isPending || dismissMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (busy) return; onOpenChange(o); }}>
      <DialogContent
        className="max-w-5xl w-full h-[90vh] max-h-[90vh] flex flex-col p-0 gap-0"
        data-testid="dialog-mail-doc-review"
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
        onInteractOutside={(e) => { if (busy) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (busy) e.preventDefault(); }}
      >
        <DialogHeader className="px-6 py-4 border-b border-gray-200 shrink-0">
          <DialogTitle className="text-base font-semibold truncate">
            Review: {doc.agentmailAttachmentName}
          </DialogTitle>
          <p className="text-xs text-gray-500 mt-0.5">
            From {doc.senderName || doc.senderEmail || "unknown"} · {doc.receivedAt ? new Date(doc.receivedAt).toLocaleDateString() : ""}
          </p>
        </DialogHeader>

        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left: file preview */}
          <div className="flex-1 min-w-0 border-r border-gray-200 bg-gray-50 flex flex-col overflow-hidden">
            {isPdf ? (
              <PdfPreview
                file={fileUrl}
                openInNewTabHref={fileUrl}
                testIdPrefix="mail-doc-pdf"
              />
            ) : (
              <div className="flex-1 min-h-0 flex items-center justify-center overflow-auto p-2">
                <img
                  src={fileUrl}
                  alt="Document preview"
                  className="max-w-full max-h-full object-contain rounded shadow"
                  data-testid="img-mail-doc-preview"
                />
              </div>
            )}
          </div>

          {/* Right: editable fields */}
          <div className="w-80 shrink-0 flex flex-col overflow-y-auto p-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Property *</label>
              <Select value={propertyId} onValueChange={setPropertyId}>
                <SelectTrigger data-testid="select-mail-doc-property">
                  <SelectValue placeholder="Select a property" />
                </SelectTrigger>
                <SelectContent>
                  {sortProperties(properties).map((p) => (
                    <SelectItem key={p.id} value={String(p.id)} data-testid={`option-property-${p.id}`}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!propertyId && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> Required to approve
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Document Type</label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="select-mail-doc-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DOC_TYPE_LABELS).map(([v, l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Vendor / Payee</label>
              <Input
                value={vendorName}
                onChange={(e) => setVendorName(e.target.value)}
                placeholder="Vendor name"
                data-testid="input-mail-doc-vendor"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Amount ($)</label>
              <Input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                data-testid="input-mail-doc-amount"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="input-mail-doc-date"
              />
            </div>

            {(docType === "invoice") && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Invoice #</label>
                <Input
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="INV-001"
                  data-testid="input-mail-doc-invoice-number"
                />
              </div>
            )}

            {(docType === "expense" || docType === "invoice") && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="select-mail-doc-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Description / Notes</label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional notes"
                data-testid="input-mail-doc-description"
              />
            </div>

            {doc.extractedData?.warnings && Array.isArray(doc.extractedData.warnings) && doc.extractedData.warnings.length > 0 && (
              <div className="text-xs text-amber-700 bg-amber-50 rounded p-2 border border-amber-200">
                <p className="font-medium mb-1">AI notes:</p>
                {doc.extractedData.warnings.map((w: string, i: number) => (
                  <p key={i}>{w}</p>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-gray-200 shrink-0 flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={() => dismissMutation.mutate()}
            disabled={dismissMutation.isPending || approveMutation.isPending}
            data-testid="button-mail-doc-dismiss"
          >
            {dismissMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <X className="h-4 w-4 mr-1" />}
            Dismiss
          </Button>
          <Button
            size="sm"
            onClick={() => approveMutation.mutate()}
            disabled={!propertyId || approveMutation.isPending || dismissMutation.isPending}
            data-testid="button-mail-doc-approve"
          >
            {approveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
            Approve & Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
